/**
 * Konfigurator Opakowań Transportowych
 * Automatyczny dobór opakowań, materiałów zabezpieczających i generowanie instrukcji
 */

import { generatePackagingPDF as generatePDF } from './generatePackagingPDF';

// Typy danych
export interface ProductDimensions {
  length: number; // mm
  width: number; // mm
  height: number; // mm
  weight: number; // kg
  quantity: number; // ilość sztuk
}

export interface MaterialType {
  id: string;
  name: string;
  fragility: 'low' | 'medium' | 'high' | 'very_high';
  thickness: number; // mm
  requiresProtection: boolean;
}

export interface PackagingType {
  id: string;
  name: string;
  internalLength: number;
  internalWidth: number;
  internalHeight: number;
  wallThickness: number;
  maxWeight: number;
  price: number;
  description: string;
}

export interface ProtectionMaterial {
  id: string;
  name: string;
  type: 'foam' | 'bubble' | 'paper' | 'airbag' | 'corner' | 'separator';
  thickness: number;
  density?: number; // kg/m³
  pricePerM2: number;
  shockAbsorption: number; // 1-10
}

export interface PackagingResult {
  box: PackagingType;
  protection: {
    material: ProtectionMaterial;
    quantity: number; // m² lub sztuki
    placement: string[];
  }[];
  totalCost: number;
  safetyFactor: number; // 1-10
  instructions: string[];
  stackingLimit: number;
  shippingClass: string;
  estimatedPreparationTime: number; // minuty
}

// Baza danych materiałów
export const MATERIALS: MaterialType[] = [
  { id: 'pmma', name: 'PMMA (Pleksi)', fragility: 'high', thickness: 3, requiresProtection: true },
  { id: 'pc', name: 'Poliwęglan', fragility: 'medium', thickness: 4, requiresProtection: true },
  { id: 'pet', name: 'PET', fragility: 'medium', thickness: 1, requiresProtection: true },
  { id: 'pvc', name: 'PVC', fragility: 'low', thickness: 3, requiresProtection: false },
  { id: 'pmma_thick', name: 'PMMA grube (>10mm)', fragility: 'medium', thickness: 15, requiresProtection: true },
  { id: 'pmma_formed', name: 'PMMA formowane', fragility: 'very_high', thickness: 5, requiresProtection: true },
];

// Baza pudełek - CENY RYNKOWE 2025 (netto PLN)
// Bazując na: tektura FEFCO 8 zł/m² + robocizna
export const PACKAGING_BOXES: PackagingType[] = [
  // Małe pudełka
  { id: 'box_s1', name: 'Karton S1', internalLength: 300, internalWidth: 200, internalHeight: 150, wallThickness: 3, maxWeight: 5, price: 4.5, description: 'Małe elementy' },
  { id: 'box_s2', name: 'Karton S2', internalLength: 400, internalWidth: 300, internalHeight: 200, wallThickness: 3, maxWeight: 8, price: 6.5, description: 'Średnie elementy' },
  
  // Średnie pudełka
  { id: 'box_m1', name: 'Karton M1', internalLength: 600, internalWidth: 400, internalHeight: 300, wallThickness: 5, maxWeight: 15, price: 12, description: 'Standardowe wyroby' },
  { id: 'box_m2', name: 'Karton M2', internalLength: 800, internalWidth: 600, internalHeight: 400, wallThickness: 5, maxWeight: 20, price: 18, description: 'Większe wyroby' },
  
  // Duże pudełka
  { id: 'box_l1', name: 'Karton L1', internalLength: 1000, internalWidth: 700, internalHeight: 500, wallThickness: 7, maxWeight: 30, price: 28, description: 'Duże płyty' },
  { id: 'box_l2', name: 'Karton L2', internalLength: 1200, internalWidth: 800, internalHeight: 600, wallThickness: 7, maxWeight: 40, price: 38, description: 'Bardzo duże elementy' },
  
  // Pudełka specjalne
  { id: 'box_xl1', name: 'Karton XL', internalLength: 1500, internalWidth: 1000, internalHeight: 800, wallThickness: 10, maxWeight: 60, price: 55, description: 'Elementy gabarytowe' },
  { id: 'box_flat', name: 'Karton płaski', internalLength: 2000, internalWidth: 1500, internalHeight: 100, wallThickness: 7, maxWeight: 50, price: 45, description: 'Płyty i arkusze' },
  
  // Palety i skrzynie
  { id: 'pallet_euro', name: 'Paleta EURO', internalLength: 1200, internalWidth: 800, internalHeight: 144, wallThickness: 20, maxWeight: 1000, price: 30, description: 'Paleta jednorazowa' },
  { id: 'crate_s', name: 'Skrzynia mała', internalLength: 1000, internalWidth: 800, internalHeight: 800, wallThickness: 20, maxWeight: 100, price: 120, description: 'Ciężkie elementy' },
  { id: 'crate_l', name: 'Skrzynia duża', internalLength: 2000, internalWidth: 1200, internalHeight: 1000, wallThickness: 20, maxWeight: 200, price: 250, description: 'Transport morski' },
];

// Materiały ochronne - CENY RYNKOWE 2025 (netto PLN)
// Bazując na cenach hurtowych
export const PROTECTION_MATERIALS: ProtectionMaterial[] = [
  { id: 'foam_5', name: 'Pianka PE 5mm', type: 'foam', thickness: 5, density: 30, pricePerM2: 2.0, shockAbsorption: 6 },
  { id: 'foam_10', name: 'Pianka PE 10mm', type: 'foam', thickness: 10, density: 30, pricePerM2: 3.8, shockAbsorption: 8 },
  { id: 'foam_20', name: 'Pianka PE 20mm', type: 'foam', thickness: 20, density: 35, pricePerM2: 7.5, shockAbsorption: 9 },
  { id: 'bubble_s', name: 'Folia bąbelkowa B1 (10mm)', type: 'bubble', thickness: 3, pricePerM2: 0.65, shockAbsorption: 4 },
  { id: 'bubble_l', name: 'Folia bąbelkowa BK (30mm)', type: 'bubble', thickness: 5, pricePerM2: 3.3, shockAbsorption: 6 },
  { id: 'paper', name: 'Papier pakowy', type: 'paper', thickness: 1, pricePerM2: 0.8, shockAbsorption: 2 },
  { id: 'airbag', name: 'Poduszki powietrzne', type: 'airbag', thickness: 50, pricePerM2: 4.5, shockAbsorption: 7 },
  { id: 'corner', name: 'Narożniki tekturowe', type: 'corner', thickness: 5, pricePerM2: 2.5, shockAbsorption: 5 },
  { id: 'separator', name: 'Przekładki PE 2mm', type: 'separator', thickness: 2, pricePerM2: 1.2, shockAbsorption: 3 },
];

/**
 * Oblicza wymagane wymiary opakowania z marginesem bezpieczeństwa
 */
export function calculateRequiredDimensions(
  product: ProductDimensions,
  material: MaterialType,
  protectionThickness: number
): { length: number; width: number; height: number } {
  // Margines bezpieczeństwa zależny od kruchości
  const safetyMargin = material.fragility === 'very_high' ? 100 :
                       material.fragility === 'high' ? 80 :
                       material.fragility === 'medium' ? 60 : 40;
  
  // Dodaj grubość ochrony z każdej strony
  const protection = protectionThickness * 2;
  
  return {
    length: product.length + protection + safetyMargin,
    width: product.width + protection + safetyMargin,
    height: (product.height * product.quantity) + protection + safetyMargin
  };
}

/**
 * Dobiera odpowiednie pudełko
 */
export function selectOptimalBox(
  requiredDimensions: { length: number; width: number; height: number },
  weight: number
): PackagingType | null {
  // Sortuj pudełka od najmniejszych
  const sortedBoxes = [...PACKAGING_BOXES].sort((a, b) => {
    const volumeA = a.internalLength * a.internalWidth * a.internalHeight;
    const volumeB = b.internalLength * b.internalWidth * b.internalHeight;
    return volumeA - volumeB;
  });
  
  // Znajdź pierwsze pasujące pudełko
  for (const box of sortedBoxes) {
    // Sprawdź czy produkt się zmieści (uwzględnij różne orientacje)
    const fits = (
      (requiredDimensions.length <= box.internalLength &&
       requiredDimensions.width <= box.internalWidth &&
       requiredDimensions.height <= box.internalHeight) ||
      (requiredDimensions.length <= box.internalLength &&
       requiredDimensions.height <= box.internalWidth &&
       requiredDimensions.width <= box.internalHeight) ||
      (requiredDimensions.width <= box.internalLength &&
       requiredDimensions.length <= box.internalWidth &&
       requiredDimensions.height <= box.internalHeight)
    );
    
    if (fits && weight <= box.maxWeight) {
      return box;
    }
  }
  
  return null;
}

/**
 * Dobiera materiały ochronne
 */
export function selectProtectionMaterials(
  material: MaterialType,
  productDimensions: ProductDimensions,
  box: PackagingType
): { material: ProtectionMaterial; quantity: number; placement: string[] }[] {
  const protection: { material: ProtectionMaterial; quantity: number; placement: string[] }[] = [];
  
  // Główny materiał ochronny zależny od kruchości
  let primaryProtection: ProtectionMaterial;
  let placement: string[] = [];
  
  if (material.fragility === 'very_high') {
    primaryProtection = PROTECTION_MATERIALS.find(m => m.id === 'foam_20')!;
    placement = ['Dół: 2 warstwy', 'Góra: 2 warstwy', 'Boki: 1 warstwa', 'Między produktami: 1 warstwa'];
  } else if (material.fragility === 'high') {
    primaryProtection = PROTECTION_MATERIALS.find(m => m.id === 'foam_10')!;
    placement = ['Dół: 2 warstwy', 'Góra: 1 warstwa', 'Boki: 1 warstwa'];
  } else if (material.fragility === 'medium') {
    primaryProtection = PROTECTION_MATERIALS.find(m => m.id === 'foam_5')!;
    placement = ['Dół: 1 warstwa', 'Góra: 1 warstwa'];
  } else {
    primaryProtection = PROTECTION_MATERIALS.find(m => m.id === 'bubble_s')!;
    placement = ['Owinięcie produktu'];
  }
  
  // Oblicz ilość materiału
  const boxSurfaceArea = 2 * (
    (box.internalLength * box.internalWidth) +
    (box.internalLength * box.internalHeight) +
    (box.internalWidth * box.internalHeight)
  ) / 1000000; // mm² na m²
  
  const quantity = material.fragility === 'very_high' ? boxSurfaceArea * 1.5 :
                   material.fragility === 'high' ? boxSurfaceArea * 1.2 :
                   boxSurfaceArea * 0.8;
  
  protection.push({
    material: primaryProtection,
    quantity: Math.ceil(quantity * 10) / 10,
    placement
  });
  
  // Dodaj narożniki dla dużych elementów
  if (productDimensions.length > 500 || productDimensions.width > 500) {
    const corners = PROTECTION_MATERIALS.find(m => m.id === 'corner')!;
    protection.push({
      material: corners,
      quantity: 8, // 8 narożników
      placement: ['Wszystkie narożniki produktu']
    });
  }
  
  // Dodaj przekładki dla wielu sztuk
  if (productDimensions.quantity > 1) {
    const separators = PROTECTION_MATERIALS.find(m => m.id === 'separator')!;
    protection.push({
      material: separators,
      quantity: productDimensions.quantity - 1,
      placement: ['Między każdą sztuką produktu']
    });
  }
  
  return protection;
}

/**
 * Oblicza współczynnik bezpieczeństwa
 */
export function calculateSafetyFactor(
  material: MaterialType,
  protection: { material: ProtectionMaterial; quantity: number }[]
): number {
  let baseSafety = 5; // Bazowy poziom
  
  // Modyfikuj na podstawie kruchości
  if (material.fragility === 'low') baseSafety = 8;
  else if (material.fragility === 'medium') baseSafety = 6;
  else if (material.fragility === 'high') baseSafety = 4;
  else if (material.fragility === 'very_high') baseSafety = 3;
  
  // Dodaj punkty za ochronę
  const totalShockAbsorption = protection.reduce((sum, p) => 
    sum + (p.material.shockAbsorption * p.quantity), 0
  );
  
  const finalSafety = Math.min(10, baseSafety + (totalShockAbsorption / 10));
  return Math.round(finalSafety * 10) / 10;
}

/**
 * Generuje instrukcje pakowania
 */
export function generatePackingInstructions(
  product: ProductDimensions,
  material: MaterialType,
  box: PackagingType,
  protection: { material: ProtectionMaterial; quantity: number; placement: string[] }[]
): string[] {
  const instructions: string[] = [];
  
  instructions.push('📦 PRZYGOTOWANIE OPAKOWANIA:');
  instructions.push(`1. Przygotuj karton ${box.name} (${box.internalLength}x${box.internalWidth}x${box.internalHeight}mm)`);
  instructions.push('2. Sprawdź czy karton nie jest uszkodzony');
  instructions.push('3. Złóż dno kartonu i zaklej taśmą pakową (krzyżowo)');
  
  instructions.push('');
  instructions.push('🛡️ UKŁADANIE WARSTW OCHRONNYCH:');
  
  let step = 4;
  protection.forEach(p => {
    instructions.push(`${step}. Ułóż ${p.material.name}:`);
    p.placement.forEach(place => {
      instructions.push(`   - ${place}`);
    });
    step++;
  });
  
  instructions.push('');
  instructions.push('📐 UMIESZCZENIE PRODUKTU:');
  instructions.push(`${step}. Ostrożnie umieść produkt w centrum opakowania`);
  step++;
  
  if (product.quantity > 1) {
    instructions.push(`${step}. Układaj kolejne sztuki z przekładkami między nimi`);
    instructions.push(`   - Ilość sztuk: ${product.quantity}`);
    instructions.push('   - NIE układaj sztuk bezpośrednio na sobie');
    step++;
  }
  
  if (material.fragility === 'high' || material.fragility === 'very_high') {
    instructions.push(`${step}. Upewnij się, że produkt nie dotyka ścianek kartonu`);
    step++;
  }
  
  instructions.push('');
  instructions.push('🔒 ZAMKNIĘCIE OPAKOWANIA:');
  instructions.push(`${step}. Wypełnij pozostałe wolne przestrzenie materiałem amortyzującym`);
  instructions.push(`${step + 1}. Zamknij karton i zaklej taśmą (min. 3 pasy)`);
  instructions.push(`${step + 2}. Wzmocnij narożniki taśmą`);
  
  instructions.push('');
  instructions.push('🏷️ OZNAKOWANIE:');
  instructions.push('• Naklej etykiety "OSTROŻNIE SZKŁO" (jeśli dotyczy)');
  instructions.push('• Oznacz górę opakowania strzałkami');
  instructions.push('• Dodaj etykietę adresową');
  
  if (material.fragility === 'high' || material.fragility === 'very_high') {
    instructions.push('• Naklej etykiety "NIE RZUCAĆ" i "UWAGA KRUCHE"');
  }
  
  return instructions;
}

/**
 * Oblicza limit sztaplowania
 */
export function calculateStackingLimit(
  box: PackagingType,
  weight: number,
  material: MaterialType
): number {
  // Bazowy limit na podstawie wytrzymałości kartonu
  let baseLimit = Math.floor(box.maxWeight / weight);
  
  // Modyfikuj na podstawie kruchości
  if (material.fragility === 'very_high') baseLimit = 1;
  else if (material.fragility === 'high') baseLimit = Math.min(2, baseLimit);
  else if (material.fragility === 'medium') baseLimit = Math.min(3, baseLimit);
  else baseLimit = Math.min(5, baseLimit);
  
  return Math.max(1, baseLimit);
}

/**
 * Określa klasę przesyłki
 */
export function determineShippingClass(
  weight: number,
  box: PackagingType,
  material: MaterialType
): string {
  const volume = (box.internalLength * box.internalWidth * box.internalHeight) / 1000000000; // m³
  const dimensionalWeight = volume * 200; // kg (standard kurierski)
  const chargeableWeight = Math.max(weight, dimensionalWeight);
  
  let shippingClass = '';
  
  if (chargeableWeight <= 1) shippingClass = 'Paczka Mini';
  else if (chargeableWeight <= 5) shippingClass = 'Paczka Mała';
  else if (chargeableWeight <= 10) shippingClass = 'Paczka Średnia';
  else if (chargeableWeight <= 20) shippingClass = 'Paczka Duża';
  else if (chargeableWeight <= 30) shippingClass = 'Paczka Gabaryt';
  else if (chargeableWeight <= 70) shippingClass = 'Paleta Euro';
  else shippingClass = 'Transport dedykowany';
  
  if (material.fragility === 'very_high' || material.fragility === 'high') {
    shippingClass += ' (PRZESYŁKA KRUCHA)';
  }
  
  return shippingClass;
}

/**
 * Główna funkcja kalkulatora
 */
export function calculatePackaging(
  product: ProductDimensions,
  material: MaterialType
): PackagingResult | null {
  // Określ grubość ochrony na podstawie kruchości
  const protectionThickness = material.fragility === 'very_high' ? 40 :
                              material.fragility === 'high' ? 30 :
                              material.fragility === 'medium' ? 20 : 10;
  
  // Oblicz wymagane wymiary
  const requiredDimensions = calculateRequiredDimensions(product, material, protectionThickness);
  
  // Dobierz pudełko
  const box = selectOptimalBox(requiredDimensions, product.weight * product.quantity);
  if (!box) return null;
  
  // Dobierz materiały ochronne
  const protection = selectProtectionMaterials(material, product, box);
  
  // Oblicz koszty
  const protectionCost = protection.reduce((sum, p) => 
    sum + (p.material.pricePerM2 * p.quantity), 0
  );
  const totalCost = box.price + protectionCost;
  
  // Oblicz współczynnik bezpieczeństwa
  const safetyFactor = calculateSafetyFactor(material, protection);
  
  // Generuj instrukcje
  const instructions = generatePackingInstructions(product, material, box, protection);
  
  // Oblicz limit sztaplowania
  const stackingLimit = calculateStackingLimit(box, product.weight * product.quantity, material);
  
  // Określ klasę przesyłki
  const shippingClass = determineShippingClass(product.weight * product.quantity, box, material);
  
  // Oszacuj czas przygotowania
  const estimatedPreparationTime = 5 + // podstawowy czas
    (product.quantity * 2) + // czas na każdą sztukę
    (material.fragility === 'very_high' ? 10 : 
     material.fragility === 'high' ? 5 : 0); // dodatkowy czas dla kruchych
  
  return {
    box,
    protection,
    totalCost: Math.round(totalCost * 100) / 100,
    safetyFactor,
    instructions,
    stackingLimit,
    shippingClass,
    estimatedPreparationTime
  };
}

// Eksportuj funkcję z nowego pliku
export { generatePackagingPDF } from './generatePackagingPDF';

/**
 * Generuje etykiety transportowe
 */
export function generateShippingLabels(
  result: PackagingResult,
  material: MaterialType
): string[] {
  const labels: string[] = [];
  
  // Podstawowe etykiety
  labels.push('⬆️ GÓRA / THIS SIDE UP');
  labels.push(`📦 ${result.shippingClass}`);
  labels.push(`⚖️ Waga: ${result.box.maxWeight} kg MAX`);
  labels.push(`📏 ${result.box.internalLength}x${result.box.internalWidth}x${result.box.internalHeight} mm`);
  
  // Etykiety ostrzegawcze
  if (material.fragility === 'very_high' || material.fragility === 'high') {
    labels.push('⚠️ UWAGA KRUCHE / FRAGILE');
    labels.push('🚫 NIE RZUCAĆ / DO NOT THROW');
    labels.push('💧 CHRONIĆ PRZED WILGOCIĄ / KEEP DRY');
  }
  
  if (material.id === 'pmma' || material.id === 'pmma_thick' || material.id === 'pmma_formed') {
    labels.push('🥂 OSTROŻNIE SZKŁO / GLASS HANDLE WITH CARE');
  }
  
  // Limit sztaplowania
  if (result.stackingLimit === 1) {
    labels.push('🚫 NIE SZTAPLOWAĆ / DO NOT STACK');
  } else {
    labels.push(`📚 MAX ${result.stackingLimit} WARSTW / LAYERS`);
  }
  
  return labels;
}

/**
 * Eksportuje wykrój pudełka do DXF (format tekstowy)
 */
export function exportBoxPatternDXF(box: PackagingType): string {
  // Uproszczony format DXF dla wykroju pudełka
  const { internalLength: l, internalWidth: w, internalHeight: h } = box;
  
  let dxf = '0\nSECTION\n2\nENTITIES\n';
  
  // Rysuj wykrój pudełka (rozwinięcie)
  // Dół
  dxf += `0\nLINE\n10\n${w + h}\n20\n${h}\n11\n${w + h + l}\n21\n${h}\n`;
  dxf += `0\nLINE\n10\n${w + h + l}\n20\n${h}\n11\n${w + h + l}\n21\n${h + w}\n`;
  dxf += `0\nLINE\n10\n${w + h + l}\n20\n${h + w}\n11\n${w + h}\n21\n${h + w}\n`;
  dxf += `0\nLINE\n10\n${w + h}\n20\n${h + w}\n11\n${w + h}\n21\n${h}\n`;
  
  // Przód
  dxf += `0\nLINE\n10\n${w + h}\n20\n${h + w}\n11\n${w + h + l}\n21\n${h + w}\n`;
  dxf += `0\nLINE\n10\n${w + h + l}\n20\n${h + w}\n11\n${w + h + l}\n21\n${h + w + h}\n`;
  dxf += `0\nLINE\n10\n${w + h + l}\n20\n${h + w + h}\n11\n${w + h}\n21\n${h + w + h}\n`;
  dxf += `0\nLINE\n10\n${w + h}\n20\n${h + w + h}\n11\n${w + h}\n21\n${h + w}\n`;
  
  // Tył
  dxf += `0\nLINE\n10\n${w + h}\n20\n0\n11\n${w + h + l}\n21\n0\n`;
  dxf += `0\nLINE\n10\n${w + h + l}\n20\n0\n11\n${w + h + l}\n21\n${h}\n`;
  dxf += `0\nLINE\n10\n${w + h + l}\n20\n${h}\n11\n${w + h}\n21\n${h}\n`;
  dxf += `0\nLINE\n10\n${w + h}\n20\n${h}\n11\n${w + h}\n21\n0\n`;
  
  // Boki
  // Lewy
  dxf += `0\nLINE\n10\n${h}\n20\n${h}\n11\n${w + h}\n21\n${h}\n`;
  dxf += `0\nLINE\n10\n${w + h}\n20\n${h}\n11\n${w + h}\n21\n${h + w}\n`;
  dxf += `0\nLINE\n10\n${w + h}\n20\n${h + w}\n11\n${h}\n21\n${h + w}\n`;
  dxf += `0\nLINE\n10\n${h}\n20\n${h + w}\n11\n${h}\n21\n${h}\n`;
  
  // Prawy
  dxf += `0\nLINE\n10\n${w + h + l}\n20\n${h}\n11\n${w + h + l + w}\n21\n${h}\n`;
  dxf += `0\nLINE\n10\n${w + h + l + w}\n20\n${h}\n11\n${w + h + l + w}\n21\n${h + w}\n`;
  dxf += `0\nLINE\n10\n${w + h + l + w}\n20\n${h + w}\n11\n${w + h + l}\n21\n${h + w}\n`;
  dxf += `0\nLINE\n10\n${w + h + l}\n20\n${h + w}\n11\n${w + h + l}\n21\n${h}\n`;
  
  // Góra
  dxf += `0\nLINE\n10\n${w + h}\n20\n${h + w + h}\n11\n${w + h + l}\n21\n${h + w + h}\n`;
  dxf += `0\nLINE\n10\n${w + h + l}\n20\n${h + w + h}\n11\n${w + h + l}\n21\n${h + w + h + w}\n`;
  dxf += `0\nLINE\n10\n${w + h + l}\n20\n${h + w + h + w}\n11\n${w + h}\n21\n${h + w + h + w}\n`;
  dxf += `0\nLINE\n10\n${w + h}\n20\n${h + w + h + w}\n11\n${w + h}\n21\n${h + w + h}\n`;
  
  // Linie zgięcia (przerywanem - typ DASHED)
  dxf += `0\nLINE\n6\nDASHED\n10\n${w + h}\n20\n0\n11\n${w + h}\n21\n${h + w + h + w}\n`;
  dxf += `0\nLINE\n6\nDASHED\n10\n${w + h + l}\n20\n0\n11\n${w + h + l}\n21\n${h + w + h + w}\n`;
  dxf += `0\nLINE\n6\nDASHED\n10\n0\n20\n${h}\n11\n${w + h + l + w + h}\n21\n${h}\n`;
  dxf += `0\nLINE\n6\nDASHED\n10\n0\n20\n${h + w}\n11\n${w + h + l + w + h}\n21\n${h + w}\n`;
  
  dxf += '0\nENDSEC\n0\nEOF\n';
  
  return dxf;
}
